import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import TimeAgo from 'react-timeago';

import formatName from '../modules/format-name';
import libravatarImage from '../modules/libravatar-image.mjs';
import FloorMap from './FloorMap';

const identityReplacements = {
    'madeleine.moller': 'madeleine.moller-stahl',
};

const sortByIdentity = ( a, b ) => {
    return a.identity.localeCompare( b.identity );
};

// Room numbers encode the floor as their first digit (e.g. "101", "314",
// "601" → floors 1, 3, 6) — this matches how room identifiers are read in
// Rooms.jsx (getRoomFloor), so a person's floor and a room's floor agree.
const getFloorNumber = ( device ) => {
    return device.ap.location.charAt( 0 );
};

const sortByFloor = ( a, b ) => {
    const floorDiff = parseInt( getFloorNumber( a ), 10 ) - parseInt( getFloorNumber( b ), 10 );

    if ( floorDiff !== 0 ) {
        return floorDiff;
    }

    return a.identity.localeCompare( b.identity );
};

const FLIP_DURATION = 320;

// Repel effect tuning — kept subtle on purpose: a small nudge, not a bounce.
// Radius is large enough to sweep across several cards in the grid at once,
// with a quadratic falloff so distant cards barely move while close ones do.
const REPEL_RADIUS = 320;
const REPEL_STRENGTH = 4;

const FloorCard = function({floorNumber, onClick}){
    return ( <div
        className = { 'device-wrapper floor-card' }
        onClick = { onClick }
        role = { 'button' }
        tabIndex = { 0 }
        title = { `Visa planritning för våning ${ floorNumber }` }
    >
        <div
            className = { 'device-photo-frame' }
        >
            <div
                className = { 'device-photo floor-card-photo' }
            >
                <span
                    className = { 'floor-card-number' }
                >
                    { floorNumber }
                </span>
            </div>
        </div>
    </div> );
};

const DeviceCard = function({device, urlParams, cardRef, repelRef}){
    if ( device.identity in identityReplacements ) {
        device.identity = identityReplacements[ device.identity ];
    }

    return ( <div
        className = { 'device-wrapper' }
        title = { device.identity }
        data-identity = { device.identity }
        ref = { cardRef }
    >
        <div
            className = { 'device-photo-frame' }
            ref = { repelRef }
        >
            <div
                className = { 'device-photo' }
            >
                <img
                    alt = { device.identity }
                    src = { libravatarImage(`${ device.identity }@stendahls.se`) }
                />
            </div>
            <div
                className = { 'device-name-tag' }
            >
                <span
                    className = { 'device-name' }
                >
                    { formatName( device.identity ) }
                </span>
                { device.locator &&
                    <span
                        className = { 'device-room' }
                    >
                        { device.ap.location }
                    </span>
                }
            </div>
        </div>
        { urlParams.get( 'debug' ) &&
            <div
                className = { 'last-seen-wrapper' }
            >
                <div
                    className = { 'location-wrapper' }
                >
                    { device.hostname }
                </div>
                { device.locator &&
                    <div
                        className = { 'location-wrapper' }
                    >
                        <div>
                            { device.mac }
                        </div>
                    </div>
                }
                <TimeAgo
                    date = { device.lastSeen * 1000 }
                />
            </div>
        }
    </div> );
};

const Peeps = function({validDevices, urlParams, sortMode}){
    const rootRef = useRef( null );
    const nodeRefs = useRef( new Map() );
    const previousRects = useRef( new Map() );
    const repelNodeRefs = useRef( new Map() );
    const [ openFloorNumber, setOpenFloorNumber ] = useState( null );

    const sortedDevices = useMemo( () => {
        const sorter = sortMode === 'floor' ? sortByFloor : sortByIdentity;

        return [ ...validDevices ].sort( sorter );
    }, [ validDevices, sortMode ] );

    const floorGroups = useMemo( () => {
        if ( sortMode !== 'floor' ) {
            return null;
        }

        const groups = new Map();

        sortedDevices.forEach( ( device ) => {
            const floorNumber = getFloorNumber( device );

            if ( !groups.has( floorNumber ) ) {
                groups.set( floorNumber, [] );
            }

            groups.get( floorNumber ).push( device );
        } );

        return [ ...groups.entries() ].sort( ( a, b ) => {
            return parseFloat( a[ 0 ] ) - parseFloat( b[ 0 ] );
        } );
    }, [ sortedDevices, sortMode ] );

    useLayoutEffect( () => {
        const nextRects = new Map();

        nodeRefs.current.forEach( ( node, mac ) => {
            if ( !node ) {
                return;
            }

            nextRects.set( mac, node.getBoundingClientRect() );
        } );

        nextRects.forEach( ( nextRect, mac ) => {
            const previousRect = previousRects.current.get( mac );
            const node = nodeRefs.current.get( mac );

            if ( !previousRect || !node ) {
                return;
            }

            const deltaX = previousRect.left - nextRect.left;
            const deltaY = previousRect.top - nextRect.top;

            if ( deltaX === 0 && deltaY === 0 ) {
                return;
            }

            node.style.transition = 'none';
            node.style.transform = `translate(${ deltaX }px, ${ deltaY }px)`;

            requestAnimationFrame( () => {
                node.style.transition = `transform ${ FLIP_DURATION }ms cubic-bezier(0.22, 1, 0.36, 1)`;
                node.style.transform = '';
            } );
        } );

        previousRects.current = nextRects;
    }, [ sortedDevices ] );

    useEffect( () => {
        const root = rootRef.current;

        if ( !root ) {
            return;
        }

        let frameRequested = false;
        let lastEvent = null;

        const applyRepel = () => {
            frameRequested = false;

            repelNodeRefs.current.forEach( ( node ) => {
                if ( !node ) {
                    return;
                }

                if ( !lastEvent ) {
                    node.style.transform = '';

                    return;
                }

                const rect = node.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;

                const dx = centerX - lastEvent.clientX;
                const dy = centerY - lastEvent.clientY;
                const distance = Math.sqrt( dx * dx + dy * dy );

                if ( distance >= REPEL_RADIUS || distance === 0 ) {
                    node.style.transform = '';

                    return;
                }

                const linearFalloff = 1 - distance / REPEL_RADIUS;
                const falloff = linearFalloff * linearFalloff;
                const pushX = ( dx / distance ) * REPEL_STRENGTH * falloff;
                const pushY = ( dy / distance ) * REPEL_STRENGTH * falloff;

                node.style.transform = `translate(${ pushX }px, ${ pushY }px)`;
            } );
        };

        const handleMouseMove = ( event ) => {
            lastEvent = event;

            if ( !frameRequested ) {
                frameRequested = true;
                requestAnimationFrame( applyRepel );
            }
        };

        const handleMouseLeave = () => {
            lastEvent = null;

            if ( !frameRequested ) {
                frameRequested = true;
                requestAnimationFrame( applyRepel );
            }
        };

        root.addEventListener( 'mousemove', handleMouseMove );
        root.addEventListener( 'mouseleave', handleMouseLeave );

        return () => {
            root.removeEventListener( 'mousemove', handleMouseMove );
            root.removeEventListener( 'mouseleave', handleMouseLeave );
        };
    }, [] );

    const makeCardRef = ( device ) => {
        return ( node ) => {
            if ( node ) {
                nodeRefs.current.set( device.mac, node );
            } else {
                nodeRefs.current.delete( device.mac );
            }
        };
    };

    const makeRepelRef = ( device ) => {
        return ( node ) => {
            if ( node ) {
                repelNodeRefs.current.set( device.mac, node );
            } else {
                repelNodeRefs.current.delete( device.mac );
            }
        };
    };

    if ( floorGroups ) {
        return ( <div
            className = { 'peeps-root' }
            ref = { rootRef }
        >
            { floorGroups.map( ( [ floorNumber, devices ] ) => {
                return ( <div
                    className = { 'floor-section' }
                    key = { floorNumber }
                >
                    <div
                        className = { 'devices-wrapper' }
                    >
                        <FloorCard
                            floorNumber = { floorNumber }
                            onClick = { () => setOpenFloorNumber( floorNumber ) }
                        />
                        { devices.map( ( device ) => {
                            return <DeviceCard
                                key = { device.mac }
                                device = { device }
                                urlParams = { urlParams }
                                cardRef = { makeCardRef( device ) }
                                repelRef = { makeRepelRef( device ) }
                            />;
                        } ) }
                    </div>
                </div> );
            } ) }
            { openFloorNumber &&
                <div
                    className = { 'floor-map-overlay' }
                    onClick = { () => setOpenFloorNumber( null ) }
                >
                    <div
                        className = { 'floor-map-overlay-content' }
                        onClick = { ( event ) => event.stopPropagation() }
                    >
                        <button
                            type = { 'button' }
                            className = { 'floor-map-overlay-close' }
                            onClick = { () => setOpenFloorNumber( null ) }
                            aria-label = { 'Stäng' }
                        >
                            ×
                        </button>
                        <h2
                            className = { 'floor-map-overlay-title' }
                        >
                            { `Våning ${ openFloorNumber }` }
                        </h2>
                        <FloorMap
                            floorNumber = { openFloorNumber }
                            validDevices = { validDevices }
                        />
                    </div>
                </div>
            }
        </div> );
    }

    return ( <div
        className = { 'peeps-root' }
        ref = { rootRef }
    >
        <div
            className = { 'devices-wrapper' }
        >
            { sortedDevices.map( ( device ) => {
                return <DeviceCard
                    key = { device.mac }
                    device = { device }
                    urlParams = { urlParams }
                    cardRef = { makeCardRef( device ) }
                    repelRef = { makeRepelRef( device ) }
                />;
            } ) }
        </div>
    </div> );
}

export default Peeps;
